/**
 * @file App.js
 * @description Componente principal (Controller).
 */
import React, { useState, useEffect, useCallback } from "react";
import { LogIn, LogOut, Loader, Shield } from "lucide-react";
import { jsPDF } from "jspdf";

// Importações da nossa estrutura MVC
import * as api from "./api/ligaApi";
import LoginPage from "./views/LoginPage";
import AdminPanel from "./views/AdminPanel";
import Dashboard from "./views/Dashboard";
import { useModal } from "./contexts/ModalContext";

export default function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState("DASHBOARD");
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // NOVO ESTADO
  const [error, setError] = useState("");
  const [ligaData, setLigaData] = useState({ equipes: [], rodadas: [] });
  const [admins, setAdmins] = useState([]);
  const modal = useModal();

  const handleLogout = useCallback(() => {
    localStorage.removeItem("adminToken");
    setIsAdmin(false);
    setAdmins([]);
    setView("DASHBOARD");
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const dadosLiga = await api.getDadosCompletos();
      setLigaData(dadosLiga);

      if (localStorage.getItem("adminToken")) {
        const dadosAdmins = await api.getAdmins();
        setAdmins(dadosAdmins);
      }
    } catch (err) {
      if (err.message.includes("Sessão inválida")) {
        handleLogout();
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (localStorage.getItem("adminToken")) {
      setIsAdmin(true);
    }
    fetchData();
  }, [fetchData]);

  // --- LÓGICA DE GERAÇÃO DE PDF ---
  const generatePdf = async (data, subTitle, filename) => {
    setIsGeneratingPdf(true); // ATIVA A ANIMAÇÃO
    try {
        const CARD_MARGIN = 15;
        const CARD_SPACING = 7;
        const HEADER_HEIGHT = 16;
        const ROW_HEIGHT = 14;
        const FOOTER_HEIGHT = 16;

        const loadImage = (url) => {
          return new Promise((resolve) => {
            if (!url) return resolve(null);
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
              resolve({
                  src: img.src,
                  width: img.width,
                  height: img.height
              });
            };
            img.onerror = () => resolve(null);
            img.src = url;
          });
        };

        const allTimes = data.flatMap((equipe) => equipe.times);
        const escudoPromises = allTimes.map((time) =>
          loadImage(api.getEscudoUrl(time.escudo_url))
        );
        const logoPromises = data.map((equipe) => loadImage(api.getEscudoUrl(equipe.logo_url)));

        const loadedEscudos = await Promise.all(escudoPromises);
        const loadedLogos = await Promise.all(logoPromises);
        
        const escudoMap = allTimes.reduce((map, time, index) => {
          if (loadedEscudos[index]) map[time.id] = loadedEscudos[index];
          return map;
        }, {});
        const logoMap = data.reduce((map, equipe, index) => {
            if(loadedLogos[index]) map[equipe.id] = loadedLogos[index];
            return map;
        }, {});

        let totalHeight = 40;
        data.forEach((equipe) => {
          totalHeight +=
            HEADER_HEIGHT +
            equipe.times.length * ROW_HEIGHT +
            FOOTER_HEIGHT +
            CARD_SPACING;
        });
        totalHeight += 20;

        const pageWidth = 210;
        const doc = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: [pageWidth, totalHeight],
        });

        let yPos = 0;

        doc.setFillColor("#27272A");
        doc.rect(0, 0, pageWidth, totalHeight, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor("#FFFFFF");
        doc.text("TORNEIO DE EQUIPES", pageWidth / 2, 22, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.setTextColor("#A1A1AA");
        doc.text(subTitle, pageWidth / 2, 30, { align: "center" });

        yPos = 42;

        for (const [index, equipe] of data.entries()) {
          const cardHeight =
            HEADER_HEIGHT + equipe.times.length * ROW_HEIGHT + FOOTER_HEIGHT;

          doc.setFillColor("#18181B");
          doc.roundedRect(
            CARD_MARGIN,
            yPos,
            pageWidth - CARD_MARGIN * 2,
            cardHeight,
            3,
            3,
            "F"
          );

          const headerYCenter = yPos + HEADER_HEIGHT / 2;
          doc.setFillColor("#1F2937");
          doc.roundedRect(
            CARD_MARGIN,
            yPos,
            pageWidth - CARD_MARGIN * 2,
            HEADER_HEIGHT,
            3,
            3,
            "F"
          );
          
          const logo = logoMap[equipe.id];
          const hasLogo = !!logo;

          if (hasLogo) {
              const maxDim = 10;
              const ratio = Math.min(maxDim / logo.width, maxDim / logo.height);
              const imgWidth = logo.width * ratio;
              const imgHeight = logo.height * ratio;
              const yOffset = headerYCenter - (imgHeight / 2);
              doc.addImage(logo.src, 'PNG', CARD_MARGIN + 4, yOffset, imgWidth, imgHeight, undefined, 'FAST');
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.setTextColor("#A1A1AA");
          doc.text(`${index + 1}º`, CARD_MARGIN + (hasLogo ? 16 : 5), headerYCenter, {
            baseline: "middle",
          });

          doc.setFont("helvetica", "bold");
          doc.setFontSize(14);
          doc.setTextColor("#FFFFFF");
          doc.text(equipe.nome, CARD_MARGIN + (hasLogo ? 29 : 18), headerYCenter, {
            baseline: "middle",
          });

          let rowY = yPos + HEADER_HEIGHT;

          // ORDENAÇÃO CORRIGIDA: PONTUAÇÃO E DEPOIS ALFABÉTICA
          const timesOrdenados = equipe.times.sort((a, b) => {
            if (b.pontuacao !== a.pontuacao) {
              return b.pontuacao - a.pontuacao;
            }
            return a.nome_time.localeCompare(b.nome_time);
          });

          for (const time of timesOrdenados) {
            doc.setDrawColor("#3F3F46");
            doc.setLineWidth(0.2);
            doc.line(CARD_MARGIN + 2, rowY, pageWidth - CARD_MARGIN - 2, rowY);

            rowY += ROW_HEIGHT;

            const escudo = escudoMap[time.id];
            if (escudo) {
              const maxDim = 10;
              const ratio = Math.min(maxDim / escudo.width, maxDim / escudo.height);
              const imgWidth = escudo.width * ratio;
              const imgHeight = escudo.height * ratio;
              doc.addImage(
                escudo.src,
                "PNG",
                CARD_MARGIN + 4,
                rowY - ROW_HEIGHT / 2 - (imgHeight/2),
                imgWidth,
                imgHeight,
                undefined,
                'FAST'
              );
            }

            const textX = CARD_MARGIN + 17;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor("#FFFFFF");
            doc.text(time.nome_time, textX, rowY - 8);
            
            if(time.is_captain) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor("#FBBF24");
                const timeNameWidth = doc.getTextWidth(time.nome_time);
                doc.text("(C)", textX + timeNameWidth + 2, rowY - 8);
            }

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#A1A1AA");
            doc.text(time.nome_cartoleiro, textX, rowY - 3.5);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor("#FFFFFF");
            doc.text(
              time.pontuacao.toFixed(2),
              pageWidth - CARD_MARGIN - 4,
              rowY - 7,
              { align: "right" }
            );
          }

          const footerY = yPos + cardHeight - FOOTER_HEIGHT;
          doc.setFillColor("#1F2937");
          doc.roundedRect(
            CARD_MARGIN,
            footerY,
            pageWidth - CARD_MARGIN * 2,
            FOOTER_HEIGHT,
            3,
            3,
            "F"
          );

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor("#A1A1AA");
          doc.text("PONTUAÇÃO TOTAL DA EQUIPE", pageWidth / 2, footerY + 7, {
            align: "center",
          });

          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.setTextColor("#FFFFFF");
          doc.text(equipe.pontuacao.toFixed(2), pageWidth / 2, footerY + 13.5, {
            align: "center",
          });

          yPos += cardHeight + CARD_SPACING;
        }

        doc.setFontSize(8);
        doc.setTextColor("#A1A1AA");
        doc.text(
          `Gerado em: ${new Date().toLocaleDateString("pt-BR")}`,
          pageWidth - CARD_MARGIN,
          totalHeight - 10,
          { align: "right" }
        );

        doc.save(`${filename}.pdf`);
    } catch (err) {
        console.error("Erro ao gerar PDF:", err);
        modal.showAlert({title: "Erro", message: "Não foi possível gerar o relatório.", type: "error"});
    } finally {
        setIsGeneratingPdf(false); // DESATIVA A ANIMAÇÃO
    }
  };

  const handleGerarPDFGeral = () => {
    const { equipes, rodadas } = ligaData;
    const calcularPontuacaoTotalTime = (timeId) =>
      rodadas
        .filter((p) => p.time_id && p.time_id.toString() === timeId.toString())
        .reduce((acc, curr) => acc + curr.pontos, 0);

    const dadosParaPdf = equipes
      .map((equipe) => {
        const timesComPontuacao = equipe.times.map((time) => ({
          ...time,
          pontuacao: calcularPontuacaoTotalTime(time.id),
        }));
        const pontuacaoTotalEquipe = timesComPontuacao.reduce(
          (acc, time) => acc + time.pontuacao,
          0
        );
        return {
          ...equipe,
          times: timesComPontuacao,
          pontuacao: pontuacaoTotalEquipe,
        };
      })
      .sort((a, b) => b.pontuacao - a.pontuacao);

    generatePdf(
      dadosParaPdf,
      "Pontuação Total Acumulada",
      "Relatorio_Geral_Acumulado"
    );
  };

  const handleGerarPDF = (rodadaNum) => {
    const { equipes, rodadas } = ligaData;
    const getPontosPorTimeNaRodada = (timeId) => {
      const ponto = rodadas.find(
        (r) =>
          r.numero === rodadaNum &&
          r.time_id &&
          r.time_id.toString() === timeId.toString()
      );
      return ponto ? ponto.pontos : 0;
    };

    const dadosParaPdf = equipes
      .map((equipe) => {
        const timesComPontuacao = equipe.times.map((time) => ({
          ...time,
          pontuacao: getPontosPorTimeNaRodada(time.id),
        }));
        const pontuacaoTotalEquipe = timesComPontuacao.reduce(
          (acc, time) => acc + time.pontuacao,
          0
        );
        return {
          ...equipe,
          times: timesComPontuacao,
          pontuacao: pontuacaoTotalEquipe,
        };
      })
      .sort((a, b) => b.pontuacao - a.pontuacao);

    generatePdf(
      dadosParaPdf,
      `Pontuação da Rodada ${rodadaNum}`,
      `Relatorio_Rodada_${rodadaNum}`
    );
  };

  const handleLogin = async (email, password) => {
    setError("");
    try {
      const data = await api.loginAdmin(email, password);
      if (data.success && data.token) {
        localStorage.setItem("adminToken", data.token);
        setIsAdmin(true);
        setView("ADMIN");
        fetchData();
      } else {
        setError(data.message || "Credenciais inválidas.");
      }
    } catch (err) {
      setError("Erro de conexão ou credenciais inválidas.");
    }
  };

  const handleAddEquipe = async (nome) => {
    try {
      await api.addEquipe(nome);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Nova equipe adicionada!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleRemoveEquipe = async (id) => {
    const confirmed = await modal.showConfirm({
      title: "Remover Equipe?",
      message:
        "Esta ação removerá a equipe e todos os seus times. Deseja continuar?",
    });

    if (confirmed) {
      try {
        await api.removeEquipe(id);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Equipe removida.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleAddTime = async (equipeId, timeData) => {
    try {
      await api.addTime({ equipe_id: equipeId, ...timeData });
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: `Time ${timeData.nome_time} adicionado.`,
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleRemoveTime = async (id) => {
    const confirmed = await modal.showConfirm({
      title: "Remover Time?",
      message: "Tem certeza de que deseja remover este time?",
    });

    if (confirmed) {
      try {
        await api.removeTime(id);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Time removido.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleFetchScores = async (numero) => {
    const result = await api.fetchScoresForRound(numero);
    fetchData();
    return result;
  };

  const handleUpdateAllTimes = async () => {
    const result = await api.updateAllTimes();
    fetchData();
    return result;
  };

  const handleResetarPontuacoes = async () => {
    const confirmed = await modal.showConfirm({
      title: "TEM A CERTEZA?",
      message:
        "Esta ação irá apagar TODAS as pontuações de TODAS as rodadas e não pode ser desfeita.",
    });

    if (confirmed) {
      try {
        const result = await api.resetarPontuacoes();
        fetchData();
        return result;
      } catch (err) {
        throw err;
      }
    }
    return null;
  };

  const handleResetScoresForRound = async (numero) => {
    const confirmed = await modal.showConfirm({
      title: `Resetar Rodada ${numero}?`,
      message: `Tem a certeza de que deseja apagar TODAS as pontuações da rodada ${numero}? Esta ação não pode ser desfeita.`,
    });

    if (confirmed) {
      try {
        const result = await api.resetScoresForRound(numero);
        fetchData();
        return result;
      } catch (err) {
        throw err;
      }
    }
    return null;
  };

  const handleAddAdmin = async ({ email, password }) => {
    try {
      await api.addAdmin({ email, password });
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Novo administrador adicionado.",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    const confirmed = await modal.showConfirm({
      title: "Apagar Administrador?",
      message:
        "Tem a certeza de que deseja apagar este utilizador administrador?",
    });
    if (confirmed) {
      try {
        await api.deleteAdmin(adminId);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Administrador apagado.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleSetCaptain = async (timeId, equipeId) => {
    try {
      await api.setCaptain(timeId, equipeId);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Capitão definido com sucesso!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleUploadLogo = async (equipeId, formData) => {
    try {
      await api.uploadLogo(equipeId, formData);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Logo da equipe atualizado com sucesso!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const renderView = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <Loader className="animate-spin text-teal-400 h-10 w-10" />
          <p className="text-gray-400 text-sm">A carregar dados...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-md mx-auto mt-10 flex items-center gap-3 bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl text-sm">
          <Shield size={18} className="flex-shrink-0" />
          {error}
        </div>
      );
    }

    switch (view) {
      case "LOGIN":
        return (
          <LoginPage onLogin={handleLogin} error={error} setView={setView} />
        );
      case "ADMIN":
        return (
          <AdminPanel
            equipes={ligaData.equipes}
            admins={admins}
            onAddEquipe={handleAddEquipe}
            onRemoveEquipe={handleRemoveEquipe}
            onAddTime={handleAddTime}
            onRemoveTime={handleRemoveTime}
            onFetchScores={handleFetchScores}
            onUpdateAllTimes={handleUpdateAllTimes}
            onResetScores={handleResetarPontuacoes}
            onResetScoresForRound={handleResetScoresForRound}
            onAddAdmin={handleAddAdmin}
            onDeleteAdmin={handleDeleteAdmin}
            onSetCaptain={handleSetCaptain}
            onUploadLogo={handleUploadLogo}
            onRefreshData={fetchData}
          />
        );
      default:
        return (
          <Dashboard
            ligaData={ligaData}
            handleGerarPDF={handleGerarPDF}
            handleGerarPDFGeral={handleGerarPDFGeral}
            isAdmin={isAdmin}
          />
        );
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans">
      {/* Overlay de geração de PDF */}
      {isGeneratingPdf && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex flex-col justify-center items-center gap-4">
          <Loader className="animate-spin text-teal-400 h-10 w-10" />
          <p className="text-white text-sm font-medium">A gerar o seu relatório...</p>
        </div>
      )}

      {/* Header sticky */}
      <header className="sticky top-0 z-40 bg-gray-900/80 backdrop-blur-md border-b border-gray-700/50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Logo */}
          <button
            onClick={() => setView("DASHBOARD")}
            className="flex items-center gap-2.5 group"
          >
            <img
              src="/logo-torneio-de-equipes.png"
              alt="Logo do Torneio de Equipes"
              className="w-8 h-8"
            />
            <span className="text-base sm:text-lg font-black tracking-tight text-white group-hover:text-teal-400 transition-colors">
              Torneio de Equipes
            </span>
          </button>

          {/* Navegação */}
          <nav className="flex items-center gap-1 sm:gap-2">
            {view !== "DASHBOARD" && (
              <button
                onClick={() => setView("DASHBOARD")}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
              >
                Dashboard
              </button>
            )}
            {isAdmin && view !== "ADMIN" && (
              <button
                onClick={() => setView("ADMIN")}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
              >
                Admin
              </button>
            )}
            {isAdmin ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 bg-red-600/15 hover:bg-red-600 border border-red-600/40 text-red-400 hover:text-white font-semibold py-1.5 px-3 text-sm rounded-lg transition-all"
              >
                <LogOut size={15} /> Sair
              </button>
            ) : (
              view !== "LOGIN" && (
                <button
                  onClick={() => setView("LOGIN")}
                  className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-1.5 px-3 text-sm rounded-lg transition-colors"
                >
                  <LogIn size={15} /> Admin
                </button>
              )
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">{renderView()}</main>
    </div>
  );
}